<template>
  <ProductModal
    :open="isOpen"
    :product="viewModel"
    :currency="currency"
    :initial-selection="initialSelection"
    :initial-quantity="initialQuantity"
    :editing="editing"
    :ordering-closed="orderingClosed"
    @close="close"
    @add="handleAdd"
  />
</template>

<script>
import { inject } from 'vue'
import { ulid } from 'ulid'
import ProductModal from '@/components/Product/ProductModal.vue'
import {
  fromV2,
  cartItemVariantOptionsToSelection,
  selectionFromVariantPreferences,
  selectionToCartItemVariantOptions,
  buildProductSlugIndex,
  cpspIdForSlug,
  slugForCpspId
} from '@/models/productViewModel'
import { v2BasePriceCents } from '@/utils/v2ProductBasePrice'
import { useToasts } from '@/composables/useToasts'
import { trackProductAdd } from '@/utils/productAnalytics'
import { readProductSlug, openProductLink, closeProductLink } from '@/utils/navigation'
import { findMergeableCartLine } from '@/utils/submittableCartItem'
import { useProductModalOpen } from '@/composables/useProductModalOpen'

// One modal for the whole V2 view. It cannot live inside a card: V2ProductCard and
// ProductContainer both set overflow-hidden, and V2Category toggles `h-0 hidden` when a category
// collapses - either would clip or unmount the modal. V2Selection mounts exactly one of these and
// hands out `v2OpenProductModal` so any card, at any depth, can drive it.
export default {
  name: 'V2ProductModalHost',

  components: { ProductModal },

  props: {
    // The V2 store snapshot (useStoreQuery's storeData) - the source for every product.
    store: {
      type: Object,
      default: null
    },
    // useCartMutation's optimistic localCartItems - the source for pre-filling an existing line.
    localCartItems: {
      type: Array,
      default: () => []
    },
    // useCartMutation's isCartSettled. The cart GET runs in parallel with the store GET, and the
    // cart is what says whether ordering is open, so the deep-link restore waits for both.
    cartSettled: {
      type: Boolean,
      default: true
    }
  },

  setup() {
    const { addToast } = useToasts()
    const v2UpdateCartItem = inject('v2UpdateCartItem', null)
    const v2ActivePricingTierId = inject('v2ActivePricingTierId', null)
    const v2CustomerMembershipTierId = inject('v2CustomerMembershipTierId', null)
    // Which package the cart is on - the source of the variant preferences a blank modal opens with.
    const v2CartPackageId = inject('v2CartPackageId', null)
    const v2CustomerVariantPreferences = inject('v2CustomerVariantPreferences', null)
    const v2OpenForOrders = inject('v2OpenForOrders', null)
    return {
      addToast,
      v2UpdateCartItem,
      v2ActivePricingTierId,
      v2CustomerMembershipTierId,
      v2CartPackageId,
      v2CustomerVariantPreferences,
      v2OpenForOrders
    }
  },

  data() {
    return {
      openCpspId: null,
      openFrontendKey: null,
      openIsUpsell: false,
      // The deep-link restore is one-shot: it must run when the snapshot lands, not again on every
      // background refetch, or a refetch would re-open a modal the customer had closed.
      deepLinkRestored: false
    }
  },

  computed: {
    isOpen() {
      return this.openCpspId != null && this.product != null
    },

    products() {
      return (this.store?.categories || []).flatMap((category) => category.products || [])
    },

    // Built from the categories rather than from `products`, because whether a listing is an upsell
    // is a property of the category it is in - and that decides which of a product's listings owns
    // the clean slug.
    slugIndex() {
      return buildProductSlugIndex(
        (this.store?.categories || []).flatMap((category) =>
          (category.products || []).map((product) => ({
            cpspId: product.cpsp_id,
            productId: product.id,
            name: product.name,
            isUpsell: category.type === 'UpsellCategory'
          }))
        )
      )
    },

    product() {
      if (this.openCpspId == null) return null
      return this.products.find((product) => String(product.cpsp_id) === String(this.openCpspId)) || null
    },

    // Only a real line matches: a stub card sends no frontend_key, so this stays null and the
    // modal opens empty on a fresh configuration.
    cartItem() {
      if (!this.openFrontendKey) return null
      return (this.localCartItems || []).find((item) => item.frontend_key === this.openFrontendKey) || null
    },

    viewModel() {
      if (!this.product) return null
      return fromV2({
        product: this.product,
        basePriceCents: v2BasePriceCents(this.product, {
          pricingTierId: this.v2ActivePricingTierId,
          membershipTierId: this.v2CustomerMembershipTierId,
          // The modal is opened FROM a card, which knows whether it was rendered in the upsell
          // category. Without it a product that is also offered as an upsell would open quoting its
          // upsell price from the ordinary store grid.
          isUpsell: this.openIsUpsell
        }),
        cartItem: this.cartItem
      })
    },

    editing() {
      return this.cartItem != null
    },

    orderingClosed() {
      return this.cartSettled && this.v2OpenForOrders === false
    },

    // The customer's saved answers, then the cart's package on top: a package's preference wins for
    // the same group, as in V1's CategoryProductStoreCartItem#combined_variant_preference_metadata.
    variantPreferences() {
      const customerPreferences = this.v2CustomerVariantPreferences || []
      if (this.v2CartPackageId == null) return customerPreferences
      const selectedPackage = (this.store?.packages || []).find(
        (item) => String(item.id) === String(this.v2CartPackageId)
      )
      return [...customerPreferences, ...(selectedPackage?.variant_preference_metadata || [])]
    },

    initialSelection() {
      const fromCartItem = cartItemVariantOptionsToSelection(this.cartItem?.cart_item_variant_options_attributes || [])
      // Only a blank configuration takes the package's preferences. An existing line already
      // carries the customer's own answer, and overwriting it on re-open would undo an override
      // every time they looked at the line again.
      if (this.editing) return fromCartItem
      return { ...selectionFromVariantPreferences(this.viewModel, this.variantPreferences), ...fromCartItem }
    },

    initialQuantity() {
      return Math.max(1, Number(this.cartItem?.quantity) || 1)
    },

    currency() {
      return this.store?.currency || this.product?.price_formatted?.currency_iso || 'USD'
    },

    // Both halves of what a restore needs: the snapshot the product is resolved from, and a cart
    // that has finished loading, so the modal opens knowing whether ordering is open.
    readyToRestoreDeepLink() {
      return Boolean(this.store) && this.cartSettled
    }
  },

  watch: {
    // The upsell modal hides itself while a product modal is on top of it, rather than stacking two
    // dialogs. Reported from `isOpen` rather than from the open request, so a request that never
    // resolves a product does not hide the upsell modal behind nothing.
    isOpen: {
      handler(open) {
        this.setProductModalOpen(open)
      },
      immediate: true
    },
    // The host resolves products out of the `store` prop, which is null for the first paint of a
    // cold load - an open fired from `mounted` would silently find no product and no-op. So the
    // inbound deep link waits for its data rather than for the DOM.
    readyToRestoreDeepLink: {
      handler(ready) {
        if (!ready || this.deepLinkRestored) return
        this.deepLinkRestored = true
        this.syncWithRoute()
      },
      immediate: true
    },

    // Covers browser Back/Forward and any external push: the URL is the source of truth for
    // whether the modal is open, so popping the entry the open pushed closes it.
    '$route.params.productSlug'() {
      this.syncWithRoute()
    }
  },

  beforeUnmount() {
    this.setProductModalOpen(false)
  },

  methods: {
    setProductModalOpen(open) {
      useProductModalOpen().setProductModalOpen(open)
    },

    open({ cpspId, frontendKey = null, isUpsell = false } = {}) {
      if (cpspId == null) return
      this.openCpspId = cpspId
      this.openFrontendKey = frontendKey
      this.openIsUpsell = Boolean(isUpsell)

      // A cpsp id with no product behind it (stale link, product pulled from the menu) leaves the
      // host half-open and `isOpen` false. Reset it and drop the param rather than leaving a URL
      // that reads as if something is open.
      if (!this.isOpen) {
        this.close()
        return
      }

      openProductLink(this.$router, this.$route, this.slugForCpspId(this.openCpspId))
    },

    close() {
      this.openCpspId = null
      this.openFrontendKey = null
      this.openIsUpsell = false
      closeProductLink(this.$router, this.$route)
    },

    slugForCpspId(cpspId) {
      return slugForCpspId(this.slugIndex, cpspId)
    },

    isUpsellCpspId(cpspId) {
      return (this.store?.categories || []).some(
        (category) =>
          category.type === 'UpsellCategory' &&
          (category.products || []).some((product) => String(product.cpsp_id) === String(cpspId))
      )
    },

    // Reconciles the modal with the URL's product segment. Idempotent on purpose: `open` itself
    // writes the segment, so this runs again on the navigation it caused and has to recognise the
    // state it is already in.
    syncWithRoute() {
      const slug = readProductSlug(this.$route)

      if (!slug) {
        if (this.openCpspId != null) this.close()
        return
      }

      const cpspId = cpspIdForSlug(this.slugIndex, slug)
      if (cpspId == null) {
        closeProductLink(this.$router, this.$route)
        return
      }
      if (String(cpspId) === String(this.openCpspId)) return

      // Match what tapping the card or its plus does: a fresh configuration, even for a product
      // already in the cart. Re-opening the existing line here let a refresh or Forward turn an
      // add into an edit, and changing an option then rewrote the line already in the cart.
      this.open({
        cpspId,
        frontendKey: null,
        isUpsell: this.isUpsellCpspId(cpspId)
      })
    },

    // The whole configuration goes out in a single v2UpdateCartItem call - quantity and every
    // modifier at once - so the debounced cart POST sees one coherent line instead of the
    // per-control dribble the inline card used to produce.
    handleAdd({ selection, quantity } = {}) {
      const product = this.product
      if (!product || this.orderingClosed) {
        this.close()
        return
      }

      const existingKey = this.cartItem?.frontend_key || null
      const wasEditing = this.editing

      trackProductAdd(this.$store, {
        isUpsell: this.openIsUpsell,
        quantity: wasEditing ? quantity - (Number(this.cartItem?.quantity) || 0) : quantity,
        productId: product.id,
        productName: product.name
      })

      const candidate = {
        frontend_key: existingKey || ulid() + (this.openIsUpsell ? '-upsell' : ''),
        category_product_store_pair_id: product.cpsp_id,
        quantity,
        cart_item_variant_options_attributes: selectionToCartItemVariantOptions(selection)
      }

      // Adding a configuration the cart already holds raises that line rather than printing a
      // second card identical to it. Only on add: re-configuring an existing line into another
      // one's shape would make the line the customer was editing vanish under them.
      const mergeable = wasEditing
        ? null
        : findMergeableCartLine(this.localCartItems, candidate, {
            ceiling: this.viewModel?.maxPerOrder,
            addedQuantity: quantity
          })

      if (this.v2UpdateCartItem) {
        this.v2UpdateCartItem(
          mergeable ? { ...mergeable.line, quantity: mergeable.quantity } : candidate,
          // The whole configuration arrives in one deliberate press, so there is nothing for the
          // debounce to coalesce - it only held the basket footer 300ms behind the toast.
          { immediate: true }
        )
      }

      this.addToast({
        message: wasEditing ? 'Cart updated' : 'Added to cart',
        type: 'success'
      })
      this.close()
    }
  }
}
</script>
